import type { FeedBoxMaterial } from '@/api/distribute'
import type { FeedCardModel } from '@/utils/distribute/card-model'

const IMAGE_X_HOST = 'img.ideaflow.pro'
const VIDEO_HOST = 'video.ideaflow.pro'
const IMAGE_X_PROCESS_QUERY = 'x-tos-process='
const IMAGE_TEMPLATE_PREFIX = '~tplv-pufud4v7fv-'
const VIDEO_UPLOAD_FRAME_TEMPLATE = '~tplv-pufud4v7fv-upload_video_frame'
const LEGACY_VIDEO_FIRST_FRAME_SUFFIX = '~tplv-pufud4v7fv-video_first_frame.jpeg'
const DEFAULT_FULL_IMAGE_WIDTH = 640
const DEFAULT_PREVIEW_IMAGE_WIDTH = 64
const DEFAULT_BLUR_RADIUS = 50
const DEFAULT_VIDEO_SNAPSHOT_TIME = 1000
const UNRESOLVED_TEMPLATE_TOKEN_RE = /\{\{\s*[^}]+\s*\}\}/

export type ImageResizeMode = 'lfit' | 'mfit' | 'fill' | 'pad' | 'fixed'
export type MediaSourceType = 'image' | 'video-frame'

export interface ImageResizeOptions {
  height?: number
  long?: number
  mode?: ImageResizeMode
  short?: number
  width?: number
}

export interface MediaTemplateSizeOptions {
  height?: number
  width?: number
}

export interface MediaRequestWidthOptions {
  dpr?: number
  fallback?: number
  max?: number
  min?: number
  step?: number
}

export interface MediaImageUrlOptions extends MediaTemplateSizeOptions {
  radius?: number
  sourceType?: MediaSourceType
}

export interface MaterialUrlOptions {
  includeCover?: boolean
  includeWatermark?: boolean
  quality?: keyof NonNullable<FeedBoxMaterial['urls']>
}

export function normalizeMediaUrl(src: string | null | undefined): string | null {
  const trimmed = src?.trim()

  if (!trimmed || UNRESOLVED_TEMPLATE_TOKEN_RE.test(trimmed)) {
    return null
  }

  return trimmed
}

function parseAbsoluteUrl(src: string): URL | null {
  try {
    return new URL(src)
  } catch {
    return null
  }
}

function isImageXAssetUrl(src: string): boolean {
  return parseAbsoluteUrl(src)?.hostname === IMAGE_X_HOST
}

function isIdeaflowAssetUrl(src: string): boolean {
  const hostname = parseAbsoluteUrl(src)?.hostname

  return Boolean(hostname && (hostname === 'ideaflow.pro' || hostname.endsWith('.ideaflow.pro')))
}

function isVideoAssetUrl(src: string): boolean {
  const pathname = parseAbsoluteUrl(src)?.pathname.toLowerCase() ?? ''

  return pathname.endsWith('.mp4') || pathname.endsWith('.mov') || pathname.endsWith('.webm')
}

function isLikelyImagePreviewAssetUrl(src: string): boolean {
  const url = parseAbsoluteUrl(src)
  const pathname = url?.pathname.toLowerCase() ?? ''

  if (
    pathname.includes(LEGACY_VIDEO_FIRST_FRAME_SUFFIX) ||
    pathname.includes(VIDEO_UPLOAD_FRAME_TEMPLATE)
  ) {
    return true
  }

  if (url?.hostname === IMAGE_X_HOST && !isVideoAssetUrl(src)) {
    return true
  }

  if (
    pathname.endsWith('.avif') ||
    pathname.endsWith('.gif') ||
    pathname.endsWith('.jpeg') ||
    pathname.endsWith('.jpg') ||
    pathname.endsWith('.png') ||
    pathname.endsWith('.webp')
  ) {
    return true
  }

  return false
}

function hasImageTemplateSuffix(src: string): boolean {
  const { body } = splitHash(src)
  const { path } = splitQuery(body)

  return path.includes(IMAGE_TEMPLATE_PREFIX)
}

function splitHash(src: string) {
  const hashIndex = src.indexOf('#')

  if (hashIndex < 0) {
    return { body: src, hash: '' }
  }

  return {
    body: src.slice(0, hashIndex),
    hash: src.slice(hashIndex),
  }
}

function splitQuery(src: string) {
  const queryIndex = src.indexOf('?')

  if (queryIndex < 0) {
    return { path: src, query: '' }
  }

  return {
    path: src.slice(0, queryIndex),
    query: src.slice(queryIndex),
  }
}

function clampImageXSize(value: number | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return null
  }

  return Math.min(16384, Math.max(1, Math.round(value)))
}

function clampTemplateSize(value: number | undefined): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0
  }

  return Math.min(16384, Math.max(0, Math.round(value)))
}

function buildResizeProcess(options: ImageResizeOptions): string | null {
  const segments = ['image/resize']
  const width = clampImageXSize(options.width)
  const height = clampImageXSize(options.height)
  const long = clampImageXSize(options.long)
  const short = clampImageXSize(options.short)
  const hasResizeDimension = width !== null || height !== null || long !== null || short !== null

  if (!hasResizeDimension) {
    return null
  }

  if (width !== null) {
    segments.push(`w_${width}`)
  }

  if (height !== null) {
    segments.push(`h_${height}`)
  }

  if (long !== null) {
    segments.push(`l_${long}`)
  }

  if (short !== null) {
    segments.push(`s_${short}`)
  }

  if (options.mode) {
    segments.push(`m_${options.mode}`)
  }

  return segments.length > 1 ? segments.join(',') : null
}

function withTemplateSuffix(src: string, suffix: string): string {
  const { body, hash } = splitHash(src)
  const { path, query } = splitQuery(body)

  return `${path}${suffix}${query}${hash}`
}

export function appendImageXProcess(
  src: string | null | undefined,
  process: string,
): string | null {
  const normalized = normalizeMediaUrl(src)

  if (!normalized || !process || !isImageXAssetUrl(normalized)) {
    return normalized
  }

  const { body, hash } = splitHash(normalized)

  if (body.includes(IMAGE_X_PROCESS_QUERY)) {
    return normalized
  }

  const separator = body.includes('?') ? '&' : '?'

  return `${body}${separator}x-tos-process=${process}${hash}`
}

function appendIdeaflowImageProcess(
  src: string | null | undefined,
  process: string,
): string | null {
  const normalized = normalizeMediaUrl(src)

  if (
    !normalized ||
    !process ||
    !isIdeaflowAssetUrl(normalized) ||
    !isLikelyImagePreviewAssetUrl(normalized)
  ) {
    return normalized
  }

  if (hasImageTemplateSuffix(normalized)) {
    return normalized
  }

  const { body, hash } = splitHash(normalized)
  const { path, query } = splitQuery(body)
  const queryParts = query ? query.slice(1).split('&').filter(Boolean) : []
  let replaced = false
  const nextQueryParts = queryParts.map((part) => {
    const [key] = part.split('=')

    if (key === 'x-tos-process') {
      replaced = true
      return `x-tos-process=${process}`
    }

    return part
  })

  if (!replaced) {
    nextQueryParts.push(`x-tos-process=${process}`)
  }

  return `${path}?${nextQueryParts.join('&')}${hash}`
}

export function buildImageResizeUrl(
  src: string | null | undefined,
  options: ImageResizeOptions,
): string | null {
  const process = buildResizeProcess(options)

  if (!process) {
    return normalizeMediaUrl(src)
  }

  return appendImageXProcess(src, process)
}

export function buildImageBlurUrl(
  src: string | null | undefined,
  options: MediaImageUrlOptions = {},
): string | null {
  const normalized = normalizeMediaUrl(src)

  if (!normalized || !isImageXAssetUrl(normalized) || !isLikelyImagePreviewAssetUrl(normalized)) {
    return normalized
  }

  if (hasImageTemplateSuffix(normalized)) {
    return normalized
  }

  const width = clampTemplateSize(options.width)
  const height = clampTemplateSize(options.height)
  const radius = Math.max(1, Math.round(options.radius ?? DEFAULT_BLUR_RADIUS))

  if (!width && !height) {
    return normalized
  }

  return withTemplateSuffix(
    normalized,
    `${IMAGE_TEMPLATE_PREFIX}blur-v1:${radius}:${width}:${height}.image`,
  )
}

export function buildImageScaleUrl(
  src: string | null | undefined,
  options: MediaTemplateSizeOptions = {},
): string | null {
  const normalized = normalizeMediaUrl(src)

  if (!normalized || !isImageXAssetUrl(normalized) || !isLikelyImagePreviewAssetUrl(normalized)) {
    return normalized
  }

  if (hasImageTemplateSuffix(normalized)) {
    return normalized
  }

  const width = clampTemplateSize(options.width)
  const height = clampTemplateSize(options.height)

  if (!width && !height) {
    return normalized
  }

  return withTemplateSuffix(
    normalized,
    `${IMAGE_TEMPLATE_PREFIX}scale-v1:${width}:${height}:q100.image`,
  )
}

export function resolveMediaRequestWidth(
  layoutWidth: number | null | undefined,
  options: MediaRequestWidthOptions = {},
): number {
  const fallback = options.fallback ?? DEFAULT_FULL_IMAGE_WIDTH
  const min = options.min ?? 160
  const max = options.max ?? 960
  const step = options.step ?? 80
  const dpr = Math.min(3, Math.max(1, options.dpr ?? 1))
  const rawWidth =
    typeof layoutWidth === 'number' && Number.isFinite(layoutWidth) && layoutWidth > 0
      ? layoutWidth * dpr
      : fallback
  const steppedWidth = Math.ceil(rawWidth / step) * step

  return Math.min(max, Math.max(min, steppedWidth))
}

export function resolveMediaPreviewWidth(layoutWidth: number | null | undefined): number {
  const baseWidth =
    typeof layoutWidth === 'number' && Number.isFinite(layoutWidth) && layoutWidth > 0
      ? layoutWidth / 4
      : DEFAULT_PREVIEW_IMAGE_WIDTH

  return Math.min(128, Math.max(48, Math.round(baseWidth)))
}

export function buildVideoFirstFrameUrl(
  src: string | null | undefined,
  options: MediaTemplateSizeOptions = {},
): string | null {
  const normalized = normalizeMediaUrl(src)

  if (!normalized) {
    return null
  }

  if (!isIdeaflowAssetUrl(normalized)) {
    return null
  }

  const url = parseAbsoluteUrl(normalized)
  const pathname = url?.pathname.toLowerCase() ?? ''

  if (
    pathname.includes(VIDEO_UPLOAD_FRAME_TEMPLATE) ||
    pathname.includes(LEGACY_VIDEO_FIRST_FRAME_SUFFIX)
  ) {
    return normalized
  }

  if (!isVideoAssetUrl(normalized)) {
    return null
  }

  const width = clampTemplateSize(options.width)
  const height = clampTemplateSize(options.height)

  if (url?.hostname === IMAGE_X_HOST) {
    return withTemplateSuffix(normalized, `${VIDEO_UPLOAD_FRAME_TEMPLATE}:${width}:${height}.jpeg`)
  }

  if (url?.hostname === VIDEO_HOST) {
    const { body, hash } = splitHash(normalized)
    const { path, query } = splitQuery(body)
    const queryParts = query ? query.slice(1).split('&').filter(Boolean) : []
    const process = `video/snapshot,t_${DEFAULT_VIDEO_SNAPSHOT_TIME},w_${width},h_${height},f_jpg,m_fast`
    const filteredQueryParts = queryParts.filter((part) => part.split('=')[0] !== 'x-tos-process')

    filteredQueryParts.push(`x-tos-process=${process}`)

    return `${path}?${filteredQueryParts.join('&')}${hash}`
  }

  return null
}

export function buildDisplayMediaImageUrl(
  src: string | null | undefined,
  options: MediaImageUrlOptions = {},
): string | null {
  if (options.sourceType === 'video-frame') {
    return buildVideoFirstFrameUrl(src, options)
  }

  return buildImageScaleUrl(src, options)
}

export function buildLowQualityImagePreviewUrl(
  src: string | null | undefined,
  options: MediaImageUrlOptions = {},
): string | null {
  const width = options.width ?? DEFAULT_PREVIEW_IMAGE_WIDTH

  if (options.sourceType === 'video-frame') {
    return buildVideoFirstFrameUrl(src, { width, height: options.height ?? 0 })
  }

  const process = buildResizeProcess({
    width,
    ...(options.height ? { height: options.height } : {}),
  })

  return process ? appendIdeaflowImageProcess(src, process) : normalizeMediaUrl(src)
}

export function resolveMaterialUrl(
  material: FeedBoxMaterial | null | undefined,
  options: MaterialUrlOptions = {},
): string | null {
  const quality = options.quality ?? 'medium'

  return (
    normalizeMediaUrl(material?.urls?.[quality]) ??
    normalizeMediaUrl(material?.src) ??
    (options.includeCover ? normalizeMediaUrl(material?.cover) : null) ??
    (options.includeWatermark ? normalizeMediaUrl(material?.watermark) : null)
  )
}

export function optimizeAvatarUrl(src: string | null | undefined): string | null {
  return buildImageResizeUrl(src, { width: 96, height: 96, mode: 'fill' })
}

export function optimizeFeedPosterUrl(src: string | null | undefined): string | null {
  return buildImageScaleUrl(src, { width: DEFAULT_FULL_IMAGE_WIDTH, height: 0 })
}

export function resolveFeedPosterUrl(
  model: FeedCardModel,
  options: MediaImageUrlOptions & { optimize?: boolean } = {},
): string | null {
  const imageOptions = { ...options, sourceType: 'image' as const }
  const poster = model.previewVideoUrl ? model.previewVideoCoverUrl : model.coverUrl

  if (poster) {
    return options.optimize ? buildDisplayMediaImageUrl(poster, imageOptions) : poster
  }

  const firstFrame = buildVideoFirstFrameUrl(model.previewVideoUrl, options)

  if (firstFrame) {
    return firstFrame
  }

  return options.optimize ? buildDisplayMediaImageUrl(model.coverUrl, imageOptions) : model.coverUrl
}
